import styled from 'styled-components/macro';
import * as React from 'react';
import { ApiOutlined, BookOutlined, SettingOutlined, FileDoneOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Button, Col, Dropdown, Menu, Radio, RadioChangeEvent, Tooltip } from 'antd';
import { useAppConfig } from '../../useAppConfig';
import { ANTD_GRAY } from '../../entity/shared/constants';
import { HOME_PAGE_INGESTION_ID } from '../../onboarding/config/HomePageOnboardingConfig';
import { useUpdateEducationStepIdsAllowlist } from '../../onboarding/useUpdateEducationStepIdsAllowlist';
import { useUserContext } from '../../context/useUserContext';
import DomainIcon from '../../domain/DomainIcon';
import { ReactComponent as IconAnalytics } from '../../../images/icon-data-analytics.svg';
import { ReactComponent as IconDataGovern } from '../../../images/icon-govern.svg';
import { ReactComponent as IconArrowDown } from '../../../images/keyboard_arrow_down.svg';
import { ReactComponent as IconHelpLink } from '../../../images/icon-help-outline.svg';
import { useBreadcrumbArray } from '../../search/context/BreadcrumbContext';

export enum IngestionTabType {
    Sources = 'Sources',
    Secrets = 'Secrets',
}

const LinkWrapper = styled.span`
    && {
        margin-right: 0px;
        vertical-align: top;
        display: inline-block;

        .anticon {
            vertical-align: top;
        }
    }
`;

const LinksWrapper = styled.div<{ areLinksHidden?: boolean }>`
    opacity: 1;
    display: flex;
    align-items: center;
    white-space: nowrap;
    transition: opacity 0.5s;

    ${(props) =>
        props.areLinksHidden &&
        `
        opacity: 0;
        width: 0;
    `}
`;

const MenuItem = styled(Menu.Item)`
    font-size: 12px;
    font-weight: bold;
    max-width: 240px;
`;

const NavTitleContainer = styled.span`
    display: flex;
    align-items: center;
    justify-content: left;
    padding: 2px;
`;

const NavTitleText = styled.span`
    margin-left: 6px;
`;

const NavTitleDescription = styled.div`
    font-size: 12px;
    font-weight: normal;
    color: ${ANTD_GRAY[7]};
`;

// const CustomSeparator = styled(Divider)`
//     height: 32px;
// `;

interface Props {
    areLinksHidden?: boolean;
    ingestionTabType?: IngestionTabType;
    handleIngestionModeChange?: (e: RadioChangeEvent) => void;
}

export function HeaderLinks(props: Props) {
    const { areLinksHidden } = props;
    const me = useUserContext();
    const { config } = useAppConfig();
    const breadcrumbArray = useBreadcrumbArray();
    const pageTypeMap = {
        HOME: 'home',
        ANALYTICS: 'analytics',
        GLOSSARY: 'glossary',
        DATASET: 'dataset',
        DOMAINS: 'domains',
        PERMISSIONS: 'permissions',
        INGESTION: 'ingestion',
        DATA_PRODUCTS: 'products',
    };

    const getPageType = () => {
        if (!breadcrumbArray || breadcrumbArray.length === 0) {
            return pageTypeMap.HOME;
        }

        // Get the last breadcrumb item (current page)
        const lastBreadcrumb = breadcrumbArray[breadcrumbArray.length - 1];
        const path = lastBreadcrumb.path?.toLowerCase() || '';
        const name = lastBreadcrumb.name?.toLowerCase() || '';

        // Define patterns for each page type
        const pageTypePatterns = [
            { type: pageTypeMap.ANALYTICS, patterns: ['analytics', '/analytics'] },
            { type: pageTypeMap.GLOSSARY, patterns: ['glossary', '/glossary'] },
            { type: pageTypeMap.DATASET, patterns: ['dataset', '/dataset'] },
            { type: pageTypeMap.DOMAINS, patterns: ['domains', 'domain', '/domains'] },
            { type: pageTypeMap.PERMISSIONS, patterns: ['permissions', 'policies', '/permissions', '/settings'] },
            { type: pageTypeMap.INGESTION, patterns: ['manage ingestion', 'ingestion', '/ingestion'] },
            {
                type: pageTypeMap.DATA_PRODUCTS,
                patterns: ['data products', 'data product', '/browse/dataproduct', '/dataproduct'],
            },
        ];

        // Find the first matching pattern
        const foundType = pageTypePatterns.find(({ patterns }) =>
            patterns.some((pattern) => name.includes(pattern) || path.includes(pattern)),
        );

        return foundType ? foundType.type : pageTypeMap.HOME;
    };

    const isAnalyticsEnabled = config?.analyticsConfig.enabled;

    // const isPoliciesEnabled = config?.policiesConfig.enabled;
    // const isIdentityManagementEnabled = config?.identityManagementConfig.enabled;
    // const isIngestionEnabled = config?.managedIngestionConfig.enabled;

    const showAnalytics = (isAnalyticsEnabled && me && me?.platformPrivileges?.viewAnalytics) || false;
    const showSettings = false; // true;
    const showIngestion = false;
    // isIngestionEnabled && me && me.platformPrivileges.manageIngestion && me.platformPrivileges.manageSecrets;
    // TODO: ask Joy/Asif for this type issue
    const showDomains = me?.platformPrivileges?.createDomains || me?.platformPrivileges?.manageDomains || false;

    useUpdateEducationStepIdsAllowlist(!!showIngestion, HOME_PAGE_INGESTION_ID);

    // Function to determine page type from breadcrumbs

    const handleHelpCenterClick = () => {
        window.parent.postMessage(
            {
                action: 'openHelpCenter',
                pageType: getPageType(),
            },
            '*',
        );
    };

    const getPageTypeFromBreadcrumbs = getPageType();

    return (
        <LinksWrapper areLinksHidden={areLinksHidden}>
            {getPageTypeFromBreadcrumbs === pageTypeMap.INGESTION && (
                <Col flex="none">
                    <Radio.Group
                        className="f-btn-tabs"
                        onChange={props.handleIngestionModeChange}
                        value={props.ingestionTabType}
                    >
                        <Radio.Button value={IngestionTabType.Sources}>Sources</Radio.Button>
                        <Radio.Button value={IngestionTabType.Secrets}>Secrets</Radio.Button>
                    </Radio.Group>
                </Col>
            )}
            {getPageTypeFromBreadcrumbs !== pageTypeMap.INGESTION && (
                <>
                    {showAnalytics && (
                        <LinkWrapper>
                            <Button
                                type="text"
                                className="mr-2"
                                aria-label="Analytics"
                                onClick={() => {
                                    window.location.href = '/analytics';
                                }}
                            >
                                <Tooltip title="View data catalog usage analytics">
                                    <NavTitleContainer>
                                        <IconAnalytics aria-hidden="true" />
                                        <NavTitleText>Analytics</NavTitleText>
                                    </NavTitleContainer>
                                </Tooltip>
                            </Button>
                        </LinkWrapper>
                    )}
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu className="f-dropdown-medium">
                                <MenuItem key="0">
                                    <Link to="/glossary">
                                        <NavTitleContainer aria-label="Glossary">
                                            <BookOutlined style={{ fontSize: '14px', fontWeight: 'bold' }} />
                                            <NavTitleText>Glossary</NavTitleText>
                                        </NavTitleContainer>
                                        <NavTitleDescription className="f-color-mid-contrast">
                                            View and modify your data dictionary
                                        </NavTitleDescription>
                                    </Link>
                                </MenuItem>
                                {showDomains && (
                                    <MenuItem key="1">
                                        <Link to="/domains">
                                            <NavTitleContainer aria-label="Domains">
                                                <DomainIcon
                                                    style={{
                                                        fontSize: 14,
                                                        fontWeight: 'bold',
                                                    }}
                                                    ariaLabel="Domains"
                                                />
                                                <NavTitleText>Domains</NavTitleText>
                                            </NavTitleContainer>
                                            <NavTitleDescription className="f-color-mid-contrast">
                                                Manage related groups of data assets
                                            </NavTitleDescription>
                                        </Link>
                                    </MenuItem>
                                )}
                                <MenuItem key="2">
                                    <Link to="/browse/dataProduct">
                                        <NavTitleContainer aria-label="Data Products">
                                            <FileDoneOutlined />
                                            <NavTitleText>Data Products</NavTitleText>
                                        </NavTitleContainer>
                                        <NavTitleDescription className="f-color-mid-contrast">
                                            Organize and manage data assets
                                        </NavTitleDescription>
                                    </Link>
                                </MenuItem>
                            </Menu>
                        }
                    >
                        <LinkWrapper>
                            <Button type="text" aria-label="Govern">
                                <IconDataGovern aria-hidden="true" /> Govern <IconArrowDown aria-hidden="true" />
                            </Button>
                        </LinkWrapper>
                    </Dropdown>
                    {showIngestion && (
                        <LinkWrapper>
                            <Link to="/ingestion">
                                <Button id={HOME_PAGE_INGESTION_ID} type="text">
                                    <Tooltip title="Connect Data Catalog to your organization's data sources">
                                        <NavTitleContainer>
                                            <ApiOutlined aria-hidden="true" />
                                            <NavTitleText>Ingestion</NavTitleText>
                                        </NavTitleContainer>
                                    </Tooltip>
                                </Button>
                            </Link>
                        </LinkWrapper>
                    )}
                    {showSettings && (
                        <LinkWrapper style={{ marginRight: 12 }}>
                            <Link to="/settings">
                                <Button type="text">
                                    <Tooltip title="Manage your data catalog settings">
                                        <SettingOutlined />
                                    </Tooltip>
                                </Button>
                            </Link>
                        </LinkWrapper>
                    )}
                </>
            )}
            <LinkWrapper>
                <Button type="text" aria-label="Help Center" onClick={handleHelpCenterClick}>
                    <Tooltip title="View data catalog usage analytics">
                        <NavTitleContainer>
                            <IconHelpLink aria-hidden="true" />
                            <NavTitleText>Help</NavTitleText>
                        </NavTitleContainer>
                    </Tooltip>
                </Button>
            </LinkWrapper>
        </LinksWrapper>
    );
}
